import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Realization {
  id: number;
  title: string;
  type: string;
  industry: string;
  scope: string;
  technology: string;
  images: string[];
  description: string;
}

interface RealizationsProps {
  realizations: Realization[];
}

const Realizations: React.FC<RealizationsProps> = ({ realizations }) => {
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const { t } = useTranslation();

  const industries = [t('realizations.all'), t('realizations.healthcare'), t('realizations.industry'), t('realizations.energy'), t('realizations.transport')];

  return (
    <section id="realizations" className="section-padding steel-bg">
      <div className="container-custom">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('realizations.title')}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('realizations.description')}
          </p>
        </div>

        {/* Industry filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {industries.map((industry, index) => (
            <button
              key={industry}
              className={`px-4 py-2 rounded-full border transition-all duration-200 ${
                index === 0
                  ? 'bg-accent-green border-accent-green text-tech-dark'
                  : 'border-gray-600 text-gray-300 hover:border-accent-green hover:text-accent-green'
              }`}
            >
              {industry}
            </button>
          ))}
        </div>

        {/* Projects grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {realizations.map((project) => (
            <div
              key={project.id}
              className="tech-card group cursor-pointer"
              onClick={() => setSelectedProject(project.id)}
            >
              {/* Image gallery */}
              <div className="mb-6">
                <div className="relative aspect-video bg-tech-dark rounded-lg overflow-hidden border border-[#2a2a2a]">
                  <div className="absolute inset-0 bg-gradient-to-br from-tech-gray to-dark-blue flex items-center justify-center">
                    <div className="text-center space-y-2">
                      <div className="text-4xl">🏭</div>
                      <p className="text-gray-400 text-sm">{project.type}</p>
                    </div>
                  </div>
                  {/* Image indicators */}
                  <div className="absolute bottom-4 right-4 flex space-x-2">
                    {project.images.map((_, index) => (
                      <div
                        key={index}
                        className={`w-2 h-2 rounded-full ${
                          index === 0 ? 'bg-accent-green' : 'bg-gray-600'
                        }`}
                      ></div>
                    ))}
                  </div>
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-accent-green/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <span className="text-accent-green font-medium">{t('realizations.showDetail')}</span>
                  </div>
                </div>
              </div>

              {/* Project info */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-accent-green transition-colors duration-300">
                    {project.title}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {project.description}
                  </p>
                </div>

                {/* Project details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 block mb-1">{t('realizations.solutionType')}</span>
                    <span className="text-white font-medium">{project.type}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 block mb-1">{t('realizations.industryLabel')}</span>
                    <span className="text-white font-medium">{project.industry}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500 block mb-1">{t('realizations.technology')}</span>
                    <span className="text-white font-medium">{project.technology}</span>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-[#2a2a2a]">
                  <span className="px-3 py-1 bg-tech-dark rounded-full text-xs text-gray-400">
                    {project.scope.split(' ')[0]}
                  </span>
                  <span className="px-3 py-1 bg-tech-dark rounded-full text-xs text-gray-400">
                    {project.scope.split(' ')[1] || ''}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Selected project detail modal */}
        {selectedProject && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2 sm:p-4">
            <div className="bg-tech-dark rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {(() => {
                const project = realizations.find(p => p.id === selectedProject);
                if (!project) return null;
                
                return (
                  <div className="p-4 sm:p-8">
                    <div className="flex justify-between items-start mb-6">
                      <h3 className="text-2xl font-bold text-white">{project.title}</h3>
                      <button
                        onClick={() => setSelectedProject(null)}
                        className="text-gray-400 hover:text-white"
                      >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Image gallery */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                      {project.images.map((image, index) => (
                        <div key={index} className="aspect-video bg-tech-dark rounded-lg border border-[#2a2a2a] flex items-center justify-center">
                          <span className="text-gray-500">{t('realizations.image')} {index + 1}</span>
                        </div>
                      ))}
                    </div>

                    {/* Project details */}
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-4">{t('realizations.techParams')}</h4>
                        <div className="space-y-3">
                          <div>
                            <span className="text-gray-500 text-sm">{t('realizations.solutionType')}</span>
                            <p className="text-white">{project.type}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 text-sm">{t('realizations.industryLabel')}</span>
                            <p className="text-white">{project.industry}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 text-sm">{t('realizations.technology')}</span>
                            <p className="text-white">{project.technology}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-4">{t('realizations.deliveryScope')}</h4>
                        <p className="text-gray-300 leading-relaxed">{project.scope}</p>
                        
                        <div className="mt-6">
                          <h4 className="text-lg font-semibold text-white mb-4">{t('realizations.projectDesc')}</h4>
                          <p className="text-gray-300 leading-relaxed">{project.description}</p>
                        </div>
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="mt-8 pt-6 border-t border-[#2a2a2a]">
                      <button className="btn-primary">
                        {t('realizations.requestSimilar')}
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:space-x-4 bg-tech-gray rounded-lg px-6 py-4 border border-[#2a2a2a]">
            <span className="text-gray-300 text-sm sm:text-base text-center">{t('realizations.ctaText')}</span>
            <button className="btn-primary text-sm whitespace-nowrap">
              {t('realizations.ctaBtn')}
            </button>
          </div>
        </div>

        {/* Partner strip */}
        <div className="mt-12 pt-8 border-t border-[#1a1a1a]" role="region" aria-label="Partners">
          <div className="flex flex-wrap items-center justify-center md:justify-between gap-4 md:gap-8 opacity-60">
            <span className="text-accent-green font-semibold tracking-wider text-xs md:text-sm">Cummins</span>
            <span className="text-accent-green font-semibold tracking-wider text-xs md:text-sm">Caterpillar</span>
            <span className="text-accent-green font-semibold tracking-wider text-xs md:text-sm">ABB</span>
            <span className="text-accent-green font-semibold tracking-wider text-xs md:text-sm">Schneider</span>
            <span className="text-accent-green font-semibold tracking-wider text-xs md:text-sm">Sinexcel</span>
            <span className="text-accent-green font-semibold tracking-wider text-xs md:text-sm">Sunwoda</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Realizations;
